import React, { useEffect, useState, useRef } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import { WaitlistCTA } from './WaitlistCTA';
import { FloatingCtaProvider, useFloatingCtaState } from './floatingCtaContext';
export function Layout() {
  return <FloatingCtaProvider data-id="element-1604">
      <LayoutInner data-id="element-1605" />
    </FloatingCtaProvider>;
}
function LayoutInner() {
  // While a section (e.g. the integration "plugs into your stack" beat) owns
  // its own primary CTA, it suppresses this global floating "Try Giga for free"
  // button so the two don't compete. It fades back in once that beat passes.
  const floatingCtaSuppressed = useFloatingCtaState();
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('up');
  // True once the orange footer enters the viewport — on mobile the floating
  // "Try Giga for free" CTA is orange too, so it disappears against the footer.
  // When we reach the footer we flip the floating button to a white-ish
  // treatment so it stays visible.
  const [overFooter, setOverFooter] = useState(false);
  const footerRef = useRef<HTMLElement | null>(null);
  const lastScrollY = useRef(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openSection, setOpenSection] = useState<string | null>(null);
  const location = useLocation();
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenSection(null);
  }, [location.pathname]);
  const toggleSection = (section: string) => setOpenSection(prev => prev === section ? null : section);
  // These routes open with a dark (#1C1510) hero, so while the header is still
  // transparent (before the user scrolls) the nav must switch to a light
  // treatment — white links and a white logo — to stay legible. Once scrolled,
  // the header turns solid white and reverts to the dark-on-light treatment.
  const darkHeroRoutes = ['/compare/chatgpt', '/security', '/demo'];
  const onDark = darkHeroRoutes.includes(location.pathname) && !isScrolled;
  // While at the very top of the homepage the hero already owns the primary
  // "Try Giga for free" CTA, so the header shows a quieter white "Sign in"
  // button until the user scrolls past it.
  const atHeroTop = location.pathname === '/' && !isScrolled;
  const navLinkClass = `text-sm font-medium transition-colors ${onDark ? 'text-white/80 hover:text-white' : 'text-neutral-600 hover:text-[#2B211A]'}`;
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrolled = currentScrollY > 20;
      setIsScrolled(scrolled);
      if (scrolled) {
        if (currentScrollY > lastScrollY.current + 5) {
          setScrollDirection('down');
          setMobileMenuOpen(false);
        } else if (currentScrollY < lastScrollY.current - 5) {
          setScrollDirection('up');
        }
      } else {
        setScrollDirection('up');
      }
      lastScrollY.current = currentScrollY;
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Watch the orange footer: once any part of it is on screen, switch the
  // floating mobile CTA to its white-ish treatment so it doesn't blend in.
  useEffect(() => {
    const el = footerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => setOverFooter(entry.isIntersecting), {
      threshold: 0
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return <div className="min-h-screen bg-[#FAF8F4] text-[#2B211A] font-body selection:bg-[#EA580C]/20 selection:text-[#2B211A]" data-id="element-1606">
      <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white/90 backdrop-blur-md border-b border-neutral-200 shadow-sm py-3' : 'bg-transparent py-5'} ${isScrolled && scrollDirection === 'down' ? '-translate-y-full' : 'translate-y-0'}`} data-id="element-1607">
        <div className="container mx-auto px-6 flex items-center justify-between" data-id="element-1608">
          <Link to="/" aria-label="Giga home" className="flex items-center gap-2 group" data-id="element-1609">
            <img src="https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png" alt="" aria-hidden="true" className={`h-7 w-7 object-contain group-hover:scale-105 transition-transform ${onDark ? '[filter:brightness(0)_invert(1)]' : ''}`} data-id="element-1610" />
            <img src="https://cdn.magicpatterns.com/uploads/fUuKba8qU1ywiSyZ4Pf9Xu/giga4x.png" alt="Giga" className={`h-6 object-contain transition-[filter] ${onDark ? '[filter:brightness(0)_invert(1)]' : ''}`} data-id="element-1611" />
          </Link>

          {/* While Giga is still a waitlist, these destinations aren't live yet.
           They look like normal nav items, but clicking any of them opens the
           waitlist instead of navigating to the (not-yet-live) page. The
           original live links/dropdowns are preserved in the comment below for
           easy restore later. */}
          <nav className="hidden md:flex items-center gap-7" data-id="element-1612">
            {['Pricing', 'Integrations', 'Security', 'Use cases', 'Resources', 'Compare'].map(label => <Link key={label} to="/signup?waitlist=1" className={navLinkClass} data-id="element-1613">
                {label}
              </Link>)}
          </nav>
          {/* RESTORE LATER — live nav (links + dropdowns):
           <nav className="hidden md:flex items-center gap-8">
           <Link to="/pricing" className={navLinkClass}>Pricing</Link>
           <Link to="/#integrations" className={navLinkClass}>Integrations</Link>
           <Link to="/security" className={navLinkClass}>Security</Link>
           Use cases / Resources / Compare dropdowns (For you, For your business;
           Blog, Research, Case Studies, Changelog; vs ChatGPT, vs OpenClaw,
           vs Claude in Slack, vs Tasklet).
           */}

          <div className="flex items-center gap-3 md:gap-4" data-id="element-1614">
            {atHeroTop ?
          // Hero already shows the primary "Try Giga for free" CTA — keep a
          // quiet white Sign in button here until the user scrolls past it.
          // While Giga is gated, Login opens the waitlist too (the /login
          // page is preserved for later — see App.tsx route).
          <WaitlistCTA spinnerClassName="text-[#2B211A]" className="hidden md:block bg-white hover:bg-neutral-50 text-[#2B211A] border border-neutral-200 px-5 py-2.5 rounded-full text-sm font-bold transition-colors shadow-sm" data-id="element-1615">
                Sign in
              </WaitlistCTA> : <>
                {/* Login is waitlisted for now — opens the waitlist instead of
                 the (preserved) /login page. */}
                <WaitlistCTA spinnerClassName="text-[#2B211A]" className={`hidden md:block ${navLinkClass}`} data-id="element-1616">
                  Log in
                </WaitlistCTA>
                <WaitlistCTA className="hidden md:block bg-[#EA580C] hover:bg-[#C2410C] text-white px-5 py-2.5 rounded-full text-sm font-bold transition-colors shadow-sm" data-id="element-1617">
                  Try Giga for free
                </WaitlistCTA>
              </>}

            {/* Mobile control: hamburger, plus a compact "Try for free" CTA.
             The header is only revealed while scrolling UP (it slides away on
             scroll-down), so showing the CTA here gives the user a quick
             conversion tap right next to the menu when they scroll back up.
             Hidden at the very top of the hero, where the hero already shows
             its own primary CTA. */}
            <div className="md:hidden flex items-center gap-2" data-id="element-1618">
              {isScrolled && <WaitlistCTA className="bg-[#EA580C] hover:bg-[#C2410C] text-white px-4 py-2 rounded-full text-sm font-bold transition-colors shadow-sm whitespace-nowrap" data-id="element-1619">
                  Try for free
                </WaitlistCTA>}
              <button className={`p-2 -mr-2 transition-colors ${onDark ? 'text-white' : 'text-[#2B211A]'}`} onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle mobile menu" aria-expanded={mobileMenuOpen} data-id="element-1620">
                {mobileMenuOpen ? <X size={24} data-id="element-1621" /> : <Menu size={24} data-id="element-1622" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown - Compact */}
        {mobileMenuOpen && <div className="md:hidden absolute top-full left-0 w-full bg-white border-b border-neutral-200 shadow-lg pb-3 px-5 flex flex-col gap-1.5 z-50 max-h-[80vh] overflow-y-auto" data-id="element-1623">
            <div className="sticky top-0 -mx-5 px-5 pt-3 pb-3 bg-white border-b border-neutral-100 flex gap-3 z-10" data-id="element-1624">
              {/* Login is waitlisted for now — opens the waitlist instead of
               the (preserved) /login page. */}
              <WaitlistCTA spinnerClassName="text-[#2B211A]" className="flex-1 text-center bg-white text-[#2B211A] border border-neutral-200 px-4 py-2.5 rounded-lg text-sm font-bold shadow-sm" data-id="element-1625">
                Log in
              </WaitlistCTA>
              <WaitlistCTA className="flex-1 text-center bg-[#EA580C] text-white px-4 py-2.5 rounded-lg text-sm font-bold shadow-sm" data-id="element-1626">
                Try Giga for free
              </WaitlistCTA>
            </div>
            {/* While Giga is still a waitlist, every nav destination opens the
             waitlist instead of its (not-yet-live) page. */}
            {['Pricing', 'Use Cases', 'Integrations', 'Security', 'Enterprise', 'Resources', 'Compare'].map(label => <Link key={label} to="/signup?waitlist=1" className="text-base font-medium text-[#2B211A] py-1.5 border-b border-neutral-100 first:mt-2" data-id="element-1627">
                {label}
              </Link>)}
          </div>}
      </header>

      <main className="flex-1" data-id="element-1628">
        <Outlet data-id="element-1629" />
      </main>

      <footer ref={footerRef} className="relative overflow-hidden bg-[#EA580C] text-sm text-white/80 pt-20 pb-40 md:pb-56" data-id="element-1630">
        {/* Subtle grid texture */}
        <div className="dot-matrix opacity-20" aria-hidden="true" data-id="element-1631" />

        {/* Oversized ghosted brand wordmark — sits in the lower band of the
         footer (below the sitemap), gently bleeding off the bottom edge. */}
        <img src="https://cdn.magicpatterns.com/uploads/fUuKba8qU1ywiSyZ4Pf9Xu/giga4x.png" alt="" aria-hidden="true" className="pointer-events-none select-none absolute inset-x-0 -bottom-[2vw] mx-auto w-[80%] max-w-none object-contain opacity-[0.12] [filter:brightness(0)_invert(1)]" data-id="element-1632" />

        <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-1633">
          {/* Sitemap */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-10 md:gap-8 pb-16 border-b border-white/20" data-id="element-1634">
            {/* Brand column */}
            <div className="col-span-2 lg:col-span-1" data-id="element-1635">
              <img src="https://cdn.magicpatterns.com/uploads/fUuKba8qU1ywiSyZ4Pf9Xu/giga4x.png" alt="Giga" className="h-7 object-contain mb-4 [filter:brightness(0)_invert(1)]" data-id="element-1636" />
              <p className="text-white/70 leading-relaxed max-w-xs" data-id="element-1637">
                The AI coworker that takes action across your work and your
                life.
              </p>
            </div>

            {[{
            title: 'Product',
            links: [{
              label: 'Pricing',
              to: '/pricing'
            }, {
              label: 'Security',
              to: '/security'
            }, {
              label: 'Enterprise',
              to: '/enterprise'
            }, {
              label: 'Giga vs ChatGPT',
              to: '/compare/chatgpt'
            }]
          }, {
            title: 'Use cases',
            links: [{
              label: 'For you',
              to: '/use-cases/founders-execs'
            }, {
              label: 'For your business',
              to: '/use-cases/client-management'
            }]
          }, {
            title: 'Resources',
            links: [{
              label: 'Blog',
              to: '/blog'
            }, {
              label: 'Research',
              to: '/research'
            }, {
              label: 'Case Studies',
              to: '/case-studies'
            }, {
              label: 'Changelog',
              to: '/changelog'
            }]
          }, {
            title: 'Company',
            links: [{
              label: 'About',
              to: '/about'
            }, {
              label: 'Careers',
              to: '/careers'
            }, {
              label: 'Contact',
              to: '/contact'
            }]
          }, {
            title: 'Legal',
            links: [{
              label: 'Privacy',
              to: '/privacy'
            }, {
              label: 'Terms',
              to: '/terms'
            }]
          }].map(col => <div key={col.title} data-id="element-1638">
                <h3 className="text-[11px] font-bold tracking-[1.5px] uppercase text-white/60 mb-4" data-id="element-1639">
                  {col.title}
                </h3>
                <ul className="space-y-3" data-id="element-1640">
                  {col.links.map(link => <li key={link.label} data-id="element-1641">
                      {/* While Giga is still a waitlist, every sitemap click
                       funnels into the waitlist. Original destination kept in
                       `link.to` for easy restore later. */}
                      <Link to="/signup?waitlist=1" className="text-white/80 hover:text-white transition-colors" data-id="element-1642">
                        {link.label}
                      </Link>
                    </li>)}
                </ul>
              </div>)}
          </div>

          {/* Bottom bar */}
          <div className="pt-8 flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-1643">
            <div className="text-white/60" data-id="element-1644">
              © {new Date().getFullYear()} Giga Inc. All rights reserved.
            </div>
            <div className="flex gap-6" data-id="element-1645">
              {[{
              label: 'Twitter',
              href: 'https://x.com/usegiga'
            }, {
              label: 'GitHub',
              href: '#'
            }, {
              label: 'LinkedIn',
              href: '#'
            }, {
              label: 'Discord',
              href: '#'
            }].map(social => {
              const external = social.href.startsWith('http');
              // Twitter points to the real profile; every other social link
              // funnels into the waitlist while Giga is still gated.
              if (!external) {
                return <Link key={social.label} to="/signup?waitlist=1" className="text-white/80 hover:text-white transition-colors" data-id="element-1646">
                      {social.label}
                    </Link>;
              }
              return <a key={social.label} href={social.href} target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white transition-colors" data-id="element-1647">
                    {social.label}
                  </a>;
            })}
            </div>
          </div>
        </div>
      </footer>
    </div>;
}